import { motion } from "framer-motion";
import {
  Phone,
  Droplets,
  Wrench,
  Filter,
  Grid3X3,
  ArrowDownCircle,
  Sparkles,
  CheckCircle,
  ArrowRight,
  AlertTriangle,
  Star,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";

const PHONE = "5599996208";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

const SERVICES_FULL = [
  {
    id: "tile",
    icon: Grid3X3,
    title: "Tile Cleaning",
    tagline: "Restore the beauty of your pool's edge.",
    star: true,
    includes: [
      "Calcium deposit removal",
      "Scale buildup cleaning",
      "Waterline tile scrubbing",
      "Grout inspection",
      "Surface polishing",
      "Sealant recommendations",
    ],
    why: "Calcium deposits and mineral buildup on your pool tiles aren't just unsightly — they can harbor bacteria and cause permanent staining if not professionally addressed.",
    signs: [
      "White crusty deposits on tiles",
      "Discolored waterline",
      "Rough texture on tile surface",
      "Visible mineral scaling",
    ],
    benefits: [
      "Restored pool aesthetics",
      "Prevented permanent staining",
      "Healthier pool environment",
      "Increased property value",
    ],
  },
  {
    id: "maintenance",
    icon: Droplets,
    title: "Pool Maintenance",
    tagline: "Consistent care, crystal clear results.",
    includes: [
      "Chemical testing and balancing",
      "Surface skimming and debris removal",
      "Brushing walls and tile line",
      "Emptying skimmer and pump baskets",
      "Equipment inspection",
    ],
    why: "Regular maintenance prevents costly repairs, keeps water safe and balanced, and extends the life of your pool equipment. A well-maintained pool is a healthy pool.",
    signs: [
      "Cloudy or discolored water",
      "Green algae forming on walls",
      "Inconsistent chemical levels",
      "Visible debris accumulation",
    ],
    benefits: [
      "Swim-ready pool on your schedule",
      "Lower long-term maintenance costs",
      "Extended equipment lifespan",
      "Safe, balanced water chemistry",
    ],
  },
  {
    id: "repair",
    icon: Wrench,
    title: "Pool Equipment Repair",
    tagline: "Expert diagnostics. Reliable fixes.",
    includes: [
      "Pump repair and replacement",
      "Heater diagnosis and repair",
      "Filter system troubleshooting",
      "Timer and automation repair",
      "Valve and plumbing fixes",
      "Motor replacement",
    ],
    why: "Pool equipment failures can escalate quickly. A malfunctioning pump or heater not only affects water quality but can lead to expensive secondary damage if left unaddressed.",
    signs: [
      "Unusual noises from the pump",
      "Pool not heating properly",
      "Water circulation issues",
      "Leaking equipment",
    ],
    benefits: [
      "Fast, accurate diagnosis",
      "Quality replacement parts",
      "Prevent secondary damage",
      "Restore full pool functionality",
    ],
  },
  {
    id: "filter",
    icon: Filter,
    title: "Filter Cleaning",
    tagline: "Better filtration. Cleaner water.",
    includes: [
      "Complete filter disassembly",
      "Deep chemical soak",
      "Grid/cartridge inspection",
      "Reassembly and pressure check",
      "O-ring inspection and lubrication",
      "Performance verification",
    ],
    why: "Filters are the heart of your pool's circulation system. A clogged or dirty filter forces your pump to work harder, increasing energy costs and reducing water clarity.",
    signs: [
      "High filter pressure readings",
      "Cloudy water despite proper chemicals",
      "Reduced water flow from returns",
      "It's been 6+ months since last cleaning",
    ],
    benefits: [
      "Improved water clarity",
      "Lower energy costs",
      "Extended filter lifespan",
      "Optimal circulation efficiency",
    ],
  },
  {
    id: "drain",
    icon: ArrowDownCircle,
    title: "Drain Clean-Ups",
    tagline: "A complete fresh start for your pool.",
    includes: [
      "Complete pool draining",
      "Acid or chlorine wash if needed",
      "TDS/CYA testing",
      "Surface inspection and stain treatment",
      "Fresh water fill",
      "Chemical startup balance",
    ],
    why: "Over time, total dissolved solids (TDS) and cyanuric acid (CYA) build up in pool water, making it harder to maintain proper chemistry. A drain and refill resets your water quality completely.",
    signs: [
      "High TDS or CYA readings",
      "Persistent staining on surfaces",
      "Water that won't balance",
      "Pool hasn't been drained in 3+ years",
    ],
    benefits: [
      "Fresh, balanced water",
      "Removed stubborn stains",
      "Reset TDS and CYA levels",
      "Clean, bright pool surface",
    ],
  },
  {
    id: "green-pool",
    icon: Sparkles,
    title: "Green Pool Recovery",
    tagline: "From swamp green to crystal clear.",
    includes: [
      "CYA level assessment (must be 30-50 ppm)",
      "Algae shock treatment",
      "Heavy debris removal",
      "Multiple chemical treatments",
      "Filter cleaning during process",
      "Final chemical balance",
    ],
    why: "A green pool isn't just an eyesore — it's a health hazard. Algae-filled water breeds bacteria and mosquitoes. We can recover your pool when CYA levels are at 30-50 ppm. Our proven recovery process restores your pool safely and quickly.",
    signs: [
      "Green or dark-colored water",
      "Visible algae on surfaces",
      "Strong unpleasant odor",
      "Can't see the pool bottom",
    ],
    benefits: [
      "Pool restored in days, not weeks",
      "Safe, swim-ready water",
      "Algae fully eliminated",
      "Prevention plan included",
    ],
  },
];

export default function ServicesPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      // Wait for page render then scroll to section
      setTimeout(() => {
        const el = document.getElementById(location.hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="overflow-hidden">
      {/* ═══ HERO ═══ */}
      <section className="relative py-32 md:py-40 bg-pool-deep overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://customer-assets.emergentagent.com/job_petes-pool-fresno/artifacts/zzv3ezi3_Screenshot%202026-03-05%20at%207.24.12%E2%80%AFPM.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-pool-deep/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-4">
              Professional Pool Care
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white tracking-tight mb-6">
              Our Services
            </h1>
            <p className="text-white/60 text-lg md:text-xl max-w-2xl mx-auto">
              Comprehensive pool maintenance and repair for every need. From
              routine upkeep to emergency restorations.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-10">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ═══ SERVICES DETAIL ═══ */}
      {SERVICES_FULL.map((service, idx) => {
        const Icon = service.icon;
        const isEven = idx % 2 === 0;
        return (
          <section
            key={service.id}
            id={service.id}
            data-testid={`service-detail-${service.id}`}
            className={`py-20 md:py-28 ${isEven ? "bg-white" : "bg-slate-50"}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div {...fadeUp}>
                {/* Header */}
                <div className="flex items-start gap-5 mb-10">
                  <div className="w-16 h-16 rounded-2xl bg-pool-aqua/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={30} className="text-pool-aqua" />
                  </div>
                  <div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold text-pool-deep tracking-tight flex items-center gap-3">
                      {service.title}
                      {service.star && <Star size={20} className="text-gold fill-gold" />}
                    </h2>
                    <p className="text-pool-aqua font-accent italic text-lg mt-1">
                      {service.tagline}
                    </p>
                  </div>
                </div>

                {/* Why it matters */}
                <p className="text-slate-600 text-base md:text-lg leading-relaxed max-w-3xl mb-12">
                  {service.why}
                </p>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* What's included */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
                    <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-pool-deep/40 mb-6">
                      What's Included
                    </h3>
                    <ul className="space-y-3">
                      {service.includes.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <CheckCircle size={16} className="text-pool-aqua flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Signs you need it */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
                    <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-pool-deep/40 mb-6">
                      Signs You Need This
                    </h3>
                    <ul className="space-y-3">
                      {service.signs.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <AlertTriangle size={16} className="text-gold flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Benefits */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
                    <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-pool-deep/40 mb-6">
                      Benefits
                    </h3>
                    <ul className="space-y-3">
                      {service.benefits.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <Sparkles size={16} className="text-pool-aqua flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        );
      })}

      {/* ═══ CTA ═══ */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-pool-aqua via-cyan-500 to-pool-aqua">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Need Pool Service?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-xl mx-auto">
              Call Pete for a free estimate. Same-week service available for most
              requests.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={`tel:${PHONE}`}
                data-testid="services-call-now"
                className="inline-flex items-center gap-3 bg-white text-pool-deep px-10 py-5 rounded-full text-lg font-bold transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <Phone size={22} />
                (559) 999-6208
              </a>
              <Link
                to="/contact"
                data-testid="services-quote-link"
                className="inline-flex items-center gap-2 bg-pool-deep text-white px-10 py-5 rounded-full text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                Contact Us <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
